/**
 * The Data panel: the graphic's fields (what PRISM users fill in on the timeline) with live test values, and each
 * field's settings: title, key, default, validation, hidden, order.
 */
import { useState, useSyncExternalStore } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, RotateCcw, Trash2 } from 'lucide-react';
import type { Field } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';
import { ensureAsset } from '../model/commands.ts';
import { moveField, removeField, sortedFields, updateField, validateValue } from '../model/fields.ts';
import { FIELD_ID } from '../model/ids.ts';
import { findElement } from '../model/tree.ts';
import { assetCache } from '../state/assets.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { ColorField, IconButton, NumberField, Row, TextField, Toggle } from '../ui/controls.tsx';

function ValueInput({ field, value, onChange, label, testId }: { field: Field; value: unknown; onChange: (v: unknown) => void; label: string; testId?: string }) {
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  switch (field.type) {
    case 'string':
      if (field.enum) {
        return (
          <label className="field select">
            <span className="field-label">{label}</span>
            <select value={String(value)} data-testid={testId} onChange={(e) => onChange(e.target.value)}>
              {field.enum.map((v) => (
                <option key={v} value={v}>
                  {v || t('data.empty')}
                </option>
              ))}
            </select>
          </label>
        );
      }
      return <TextField label={label} multiline={field.multiline} value={String(value ?? '')} testId={testId} onChange={onChange} />;
    case 'number':
      return <NumberField label={label} value={typeof value === 'number' ? value : undefined} step={field.integer ? 1 : 0.1} testId={testId} onChange={onChange} />;
    case 'boolean':
      return <Toggle label={label} checked={value === true} testId={testId} onChange={onChange} />;
    case 'color':
      return <ColorField label={label} value={typeof value === 'string' ? value : field.default} testId={testId} onChange={onChange} />;
    case 'image-choice':
      return (
        <label className="field select">
          <span className="field-label">{label}</span>
          <select value={String(value)} data-testid={testId} onChange={(e) => onChange(e.target.value)}>
            {field.choices.map((h) => (
              <option key={h} value={h}>
                {assetCache.byHash(h)?.originalName ?? h.slice(7, 15)}
              </option>
            ))}
          </select>
        </label>
      );
  }
}

function FieldCard({ store, field, index, count }: { store: EditorStore; field: Field; index: number; count: number }) {
  const scene = useEditor(store, sceneView);
  const testData = useEditor(store, (s) => s.testData);
  const [open, setOpen] = useState(false);
  const [idText, setIdText] = useState(field.id);
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const has = Object.prototype.hasOwnProperty.call(testData, field.id);
  const value = has ? testData[field.id] : field.default;
  const problem = has ? validateValue(field, value) : undefined;
  const set = (patch: Partial<Field>, key: string) => store.apply((s) => updateField(s, field.id, patch), { key: `field:${field.id}:${key}` });
  const targets = field.bindings.map((b) => `${findElement(scene, b.element)?.name ?? b.element} · ${t(`binding.${b.property}`)}`).join(', ');
  const len = field.type === 'string' && typeof value === 'string' ? [...value].length : undefined;
  const images = assetCache.library.filter((a) => a.kind === 'image');
  return (
    <li className="field-card" data-testid="field-card" data-field-id={field.id}>
      <div className="field-card-head">
        <button type="button" className="section-toggle" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="field-settings-toggle">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <span className="field-title">{field.title}</span>
          <span className="badge">{t(`fieldType.${field.type}`)}</span>
          {field.hidden && <span className="badge muted">{t('data.hidden')}</span>}
        </button>
        <IconButton label={t('data.moveUp')} onClick={() => store.apply((s) => moveField(s, field.id, -1))} disabled={index === 0}>
          <ArrowUp size={13} />
        </IconButton>
        <IconButton label={t('data.moveDown')} onClick={() => store.apply((s) => moveField(s, field.id, 1))} disabled={index === count - 1}>
          <ArrowDown size={13} />
        </IconButton>
      </div>
      <div className="field-test">
        <ValueInput field={field} value={value} label={t('data.testValue')} testId={`test-value-${field.id}`} onChange={(v) => store.setTestValue(field.id, v)} />
        <div className="field-test-meta">
          {len !== undefined && field.type === 'string' && (
            <span className={field.maxLength !== undefined && len > field.maxLength ? 'warn' : 'muted'} data-testid={`length-${field.id}`}>
              {field.maxLength !== undefined ? `${len} / ${field.maxLength}` : `${len}`}
            </span>
          )}
          {has && (
            <button type="button" className="link" onClick={() => store.setTestValue(field.id, undefined)}>
              {t('data.useDefault')}
            </button>
          )}
        </div>
        {problem && (
          <p className="hint warn" role="status">
            {problem}
          </p>
        )}
      </div>
      {open && (
        <div className="field-settings">
          <TextField label={t('data.title')} value={field.title} testId={`field-title-${field.id}`} onChange={(title) => title.trim() && set({ title }, 'title')} />
          <label className="field text">
            <span className="field-label">{t('data.key')}</span>
            <input
              type="text"
              value={idText}
              spellCheck={false}
              aria-invalid={!FIELD_ID.test(idText) || (idText !== field.id && scene.fields.some((f) => f.id === idText))}
              onChange={(e) => setIdText(e.target.value)}
              onBlur={() => {
                if (idText !== field.id && FIELD_ID.test(idText) && !scene.fields.some((f) => f.id === idText)) {
                  store.apply((s) => updateField(s, field.id, { id: idText }));
                  store.setTestValue(idText, testData[field.id]);
                  store.setTestValue(field.id, undefined);
                } else setIdText(field.id);
              }}
            />
          </label>
          <ValueInput field={field} value={field.default} label={t('data.default')} onChange={(v) => set({ default: v } as Partial<Field>, 'default')} />
          {field.type === 'string' && (
            <>
              <Row className="two">
                <NumberField label={t('data.maxLength')} value={field.maxLength} min={1} placeholder="–" testId={`max-length-${field.id}`} onChange={(v) => set({ maxLength: v ? Math.max(Math.round(v), [...field.default].length) : undefined } as Partial<Field>, 'maxLength')} />
                <Toggle label={t('data.multiline')} checked={field.multiline} onChange={(multiline) => set({ multiline } as Partial<Field>, 'multiline')} />
              </Row>
              <TextField
                label={t('data.enum')}
                placeholder={t('data.enumHint')}
                value={(field.enum ?? []).join(', ')}
                onChange={(v) => {
                  const list = v.split(',').map((x) => x.trim()).filter(Boolean);
                  const enumList = list.length ? (list.includes(field.default) ? list : [field.default, ...list]) : undefined;
                  set({ enum: enumList } as Partial<Field>, 'enum');
                }}
              />
            </>
          )}
          {field.type === 'number' && (
            <Row className="two">
              <NumberField label={t('data.minimum')} value={field.minimum} placeholder="–" onChange={(minimum) => set({ minimum: Math.min(minimum, field.default) } as Partial<Field>, 'min')} />
              <NumberField label={t('data.maximum')} value={field.maximum} placeholder="–" onChange={(maximum) => set({ maximum: Math.max(maximum, field.default) } as Partial<Field>, 'max')} />
            </Row>
          )}
          {field.type === 'image-choice' && (
            <fieldset className="choices">
              <legend className="field-label">{t('data.choices')}</legend>
              {images.map((a) => {
                const on = field.choices.includes(a.hash);
                return (
                  <Toggle
                    key={a.hash}
                    label={a.originalName}
                    checked={on}
                    onChange={(v) => {
                      const choices = v ? [...field.choices, a.hash] : field.choices.filter((c) => c !== a.hash);
                      if (choices.length === 0 || (!v && a.hash === field.default)) return;
                      store.apply((s) => updateField(v ? ensureAsset(s, a.sceneAsset) : s, field.id, { choices } as Partial<Field>));
                    }}
                  />
                );
              })}
            </fieldset>
          )}
          <TextField label={t('data.description')} value={field.description ?? ''} onChange={(description) => set({ description: description || undefined }, 'description')} />
          <Toggle label={t('data.hiddenLabel')} checked={field.hidden} onChange={(hidden) => set({ hidden }, 'hidden')} />
          <p className="hint">{t('data.boundTo', { targets: targets || t('data.unbound') })}</p>
          <button type="button" className="btn small danger" onClick={() => store.apply((s) => removeField(s, field.id))} data-testid={`remove-field-${field.id}`}>
            <Trash2 size={13} aria-hidden /> {t('data.remove')}
          </button>
        </div>
      )}
    </li>
  );
}

export function DataPanel({ store }: { store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  const testData = useEditor(store, (s) => s.testData);
  const fields = sortedFields(scene);
  return (
    <div className="data-panel" data-testid="data-panel">
      <div className="panel-title-row">
        <h2 className="panel-title">{t('data.title.panel')}</h2>
        <button type="button" className="btn small ghost" disabled={Object.keys(testData).length === 0} onClick={() => store.resetTestData()} data-testid="reset-test-data">
          <RotateCcw size={13} aria-hidden /> {t('data.reset')}
        </button>
      </div>
      <p className="hint pad">{t('data.hint')}</p>
      {fields.length === 0 ? (
        <p className="empty pad">{t('data.none')}</p>
      ) : (
        <ul className="field-list">
          {fields.map((f, i) => (
            <FieldCard key={f.id} store={store} field={f} index={i} count={fields.length} />
          ))}
        </ul>
      )}
    </div>
  );
}
